/**
 * CelebrationPage.js — Public "Celebration Book" landing page.
 *
 * No login required. A marketing/sales page that sells the idea of creating a
 * book ABOUT someone (Dad, Mom, a grandparent) with stories contributed by the
 * whole family, then printed as a hardcover and given as a gift.
 *
 * This is distinct from /gift (which gifts a person the tool to write their OWN
 * story). Here, the givers do the writing and the recipient receives a finished
 * book. The host needs an account to build + curate the book, so every CTA routes
 * into sign-up on the main app.
 *
 * Route (index.js): /celebration
 */

import React from 'react';
import { trackEvent } from './utils/analytics';
import Reviews from './Reviews';

// Celebration books are built + ordered inside the app, so CTAs go to sign-up.
const START_URL = '/?intent=celebration';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600&family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

.cb-page { min-height: 100svh; background: #fbf8f1; display: flex; flex-direction: column; align-items: center; }

/* Header */
.cb-header {
  position: sticky; top: 0; z-index: 50; width: 100%; padding: 18px 32px;
  display: flex; justify-content: space-between; align-items: center; gap: 16px;
  background: rgba(251,248,241,0.92); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(15,23,42,0.08); box-shadow: 0 2px 16px rgba(15,23,42,0.04);
}
.cb-logo { font-family: 'Lora', serif; font-size: 1rem; letter-spacing: 4px; color: #3c4653; text-transform: uppercase; font-weight: 600; text-decoration: none; }
.cb-nav-link { font-family: 'Lora', serif; font-size: 0.85rem; color: #7c2d12; text-decoration: none; font-weight: 600; white-space: nowrap; }
.cb-nav-link:hover { text-decoration: underline; }

.cb-main { width: 100%; max-width: 760px; padding: 48px 24px 96px; display: flex; flex-direction: column; align-items: center; }

/* Hero */
.cb-hero { text-align: center; margin-bottom: 36px; }
.cb-eyebrow { font-family: 'Lora', serif; font-size: 0.7rem; letter-spacing: 3px; color: #c9a84c; text-transform: uppercase; font-weight: 600; margin-bottom: 16px; }
.cb-title { font-family: 'Cinzel', serif; font-size: 2.2rem; font-weight: 500; color: #0f172a; line-height: 1.25; margin-bottom: 18px; }
.cb-title em { font-style: italic; color: #7c2d12; }
.cb-sub { font-family: 'Lora', serif; font-size: 1.1rem; color: #3c4653; line-height: 1.7; max-width: 540px; margin: 0 auto 28px; }

.cb-cta {
  display: inline-block; font-family: 'Lora', serif; font-size: 1.05rem; font-weight: 700;
  color: #1a1206; background: linear-gradient(180deg, #d9b75f, #c9a84c); border: none; border-radius: 14px;
  padding: 16px 36px; text-decoration: none; cursor: pointer; box-shadow: 0 6px 22px rgba(201,168,76,0.35);
  transition: transform 120ms, box-shadow 120ms;
}
.cb-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 26px rgba(201,168,76,0.45); }
.cb-cta-note { font-family: 'Lora', serif; font-size: 0.85rem; color: #6b7280; margin-top: 12px; }
.cb-switch { font-family: 'Lora', serif; font-size: 0.9rem; color: #6b7280; line-height: 1.5; max-width: 540px; margin: 20px auto 0; }
.cb-switch b { color: #3c4653; }
.cb-switch a { color: #7c2d12; font-weight: 700; text-decoration: none; }
.cb-switch a:hover { text-decoration: underline; }

/* Hero photo */
.cb-photo { width: 100%; margin: 8px 0 40px; }
.cb-photo img { width: 100%; display: block; border-radius: 16px; box-shadow: 0 14px 44px rgba(15,23,42,0.14); }
.cb-photo-caption { font-family: 'Lora', serif; font-style: italic; font-size: 0.9rem; color: #3c4653; text-align: center; margin-top: 14px; }

/* Section heading */
.cb-section { width: 100%; margin-bottom: 48px; }
.cb-section-head { text-align: center; margin-bottom: 28px; }
.cb-section-title { font-family: 'Cinzel', serif; font-size: 1.5rem; color: #0f172a; margin-bottom: 8px; }
.cb-section-sub { font-family: 'Lora', serif; font-size: 1rem; color: #3c4653; line-height: 1.6; max-width: 520px; margin: 0 auto; }

/* Steps */
.cb-steps { display: flex; flex-direction: column; gap: 14px; }
.cb-step { display: flex; gap: 18px; align-items: flex-start; background: #fff; border: 1px solid rgba(15,23,42,0.08); border-radius: 14px; padding: 22px 24px; }
.cb-step-num { font-family: 'Cinzel', serif; font-size: 1.4rem; color: #7c2d12; flex-shrink: 0; width: 32px; text-align: center; }
.cb-step-body { min-width: 0; }
.cb-step-title { font-family: 'Lora', serif; font-size: 1.05rem; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
.cb-step-text { font-family: 'Lora', serif; font-size: 0.95rem; color: #3c4653; line-height: 1.55; }
.cb-step-text b { color: #0f172a; }

/* Occasions */
.cb-occasions { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.cb-occasion { background: #fff; border: 1px solid rgba(15,23,42,0.08); border-radius: 12px; padding: 16px 18px; text-align: center; }
.cb-occasion-emoji { font-size: 1.6rem; display: block; margin-bottom: 8px; }
.cb-occasion-text { font-family: 'Lora', serif; font-size: 0.95rem; color: #0f172a; font-weight: 600; }

/* Quote / social proof */
.cb-quote { width: 100%; background: #fff; border: 1px solid rgba(124,45,18,0.16); border-radius: 18px; padding: 36px 32px; text-align: center; margin-bottom: 48px; }
.cb-quote-mark { font-family: 'Cinzel', serif; font-size: 2.6rem; color: #c9a84c; line-height: 1; margin-bottom: 8px; }
.cb-quote-text { font-family: 'Lora', serif; font-style: italic; font-size: 1.25rem; color: #1a1612; line-height: 1.6; max-width: 560px; margin: 0 auto 16px; }
.cb-quote-by { font-family: 'Lora', serif; font-size: 0.9rem; color: #6b7280; }

/* Final CTA band */
.cb-final { width: 100%; text-align: center; background: linear-gradient(180deg, #7c2d12, #5a1f0c); border-radius: 20px; padding: 48px 32px; }
.cb-final-title { font-family: 'Cinzel', serif; font-size: 1.7rem; color: #fbf8f1; margin-bottom: 14px; line-height: 1.3; }
.cb-final-sub { font-family: 'Lora', serif; font-size: 1.02rem; color: #f0e6d8; line-height: 1.6; max-width: 480px; margin: 0 auto 26px; }
.cb-final .cb-cta { box-shadow: 0 6px 22px rgba(0,0,0,0.25); }

/* FAQ */
.cb-faq { width: 100%; margin-bottom: 16px; }
.cb-faq-item { border-top: 1px solid rgba(15,23,42,0.08); }
.cb-faq-item:last-child { border-bottom: 1px solid rgba(15,23,42,0.08); }
.cb-faq-q { font-family: 'Lora', serif; font-size: 0.98rem; font-weight: 700; color: #0f172a; padding: 16px 4px; cursor: pointer; list-style: none; display: flex; justify-content: space-between; align-items: center; gap: 14px; }
.cb-faq-q:hover { color: #7c2d12; }
.cb-faq-q::-webkit-details-marker { display: none; }
.cb-faq-q::after { content: '+'; font-size: 1.4rem; font-weight: 400; color: #7c2d12; flex-shrink: 0; line-height: 1; }
.cb-faq-item[open] > .cb-faq-q::after { content: '\\2212'; }
.cb-faq-a { font-family: 'Lora', serif; font-size: 0.92rem; line-height: 1.55; color: #3c4653; padding: 0 4px 18px; }

/* Footer */
.cb-footer { width: 100%; padding: 28px 32px; text-align: center; border-top: 1px solid rgba(15,23,42,0.08); }
.cb-footer-text { font-family: 'Lora', serif; font-size: 0.75rem; color: #6b7280; letter-spacing: 1px; }
.cb-footer-link { color: #3c4653; text-decoration: none; }
.cb-footer-social { display: flex; gap: 16px; align-items: center; justify-content: center; margin-top: 14px; }
.cb-footer-social a { display: inline-flex; color: #3c4653; transition: color 150ms; }
.cb-footer-social a:hover { color: #0f172a; }
.cb-footer-social svg { width: 20px; height: 20px; display: block; }

@media (max-width: 640px) {
  .cb-title { font-size: 1.7rem; }
  .cb-occasions { grid-template-columns: 1fr; }
  .cb-main { padding: 36px 18px 72px; }
}
`;

function CelebrationPage() {
  const go = (location) => () => trackEvent('celebration_cta_click', { location });

  return (
    <>
      <style>{STYLES}</style>
      <div className="cb-page">
        <div className="cb-header">
          <a href="/" className="cb-logo">Talebound</a>
          <a href="/" className="cb-nav-link">Explore Talebound</a>
        </div>

        <div className="cb-main">
          {/* Hero */}
          <div className="cb-hero">
            <div className="cb-eyebrow">A Celebration Book</div>
            <h1 className="cb-title">Everyone's favorite memories of them, <em>in one book.</em></h1>
            <p className="cb-sub">
              Family and friends each write their stories of the person you're celebrating, the
              moments that mattered, the lessons, the love. We bind it all into one beautiful
              hardcover, so they can see just how much they've meant to everyone.
            </p>
            <a href={START_URL} className="cb-cta" onClick={go('hero')}>Start a Celebration Book</a>
            <div className="cb-cta-note">Free to start. You only pay when you order the printed book.</div>
            <p className="cb-switch">
              A Celebration Book is written <b>by the family, about them</b>. Want them to write
              their own story instead? <a href="/gift">Gift Talebound &rarr;</a>
            </p>
          </div>

          <div className="cb-photo">
            <img src="/talebound-books-trio.jpg" alt="Three real Talebound hardcover books in charcoal, burgundy, and floral cover styles on a table" />
            <div className="cb-photo-caption">Everyone's stories, together in one beautiful hardcover. You choose the cover.</div>
          </div>

          {/* How it works */}
          <div className="cb-section">
            <div className="cb-section-head">
              <h2 className="cb-section-title">How it works</h2>
              <p className="cb-section-sub">You organize it. Everyone contributes. We turn it into a real book.</p>
            </div>
            <div className="cb-steps">
              <div className="cb-step">
                <div className="cb-step-num">1</div>
                <div className="cb-step-body">
                  <div className="cb-step-title">Start the book and pick the occasion</div>
                  <div className="cb-step-text">Create a free account, choose who it's for, and pick the moment, a birthday, retirement, anniversary, or just because.</div>
                </div>
              </div>
              <div className="cb-step">
                <div className="cb-step-num">2</div>
                <div className="cb-step-body">
                  <div className="cb-step-title">Invite the family to contribute</div>
                  <div className="cb-step-text">Share a link with siblings, kids, and old friends. Each one shares a memory of the person you're celebrating, <b>no account needed</b>. They can type or just talk, and Talebound shapes their words into a chapter.</div>
                </div>
              </div>
              <div className="cb-step">
                <div className="cb-step-num">3</div>
                <div className="cb-step-body">
                  <div className="cb-step-title">You arrange and curate</div>
                  <div className="cb-step-text">Every contribution lands in one place. You choose what goes in, add photos, and order the chapters. You're the editor of the whole book.</div>
                </div>
              </div>
              <div className="cb-step">
                <div className="cb-step-num">4</div>
                <div className="cb-step-body">
                  <div className="cb-step-title">Give the finished hardcover</div>
                  <div className="cb-step-text">When it's ready, order a premium hardcover printed in full color. It ships to your door, and you give them a gift they could never buy for themselves.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Who it's for */}
          <div className="cb-section">
            <div className="cb-section-head">
              <h2 className="cb-section-title">For the person who says "don't get me anything"</h2>
              <p className="cb-section-sub">A Celebration Book fits any moment worth remembering.</p>
            </div>
            <div className="cb-occasions">
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'\u{1F382}'}</span><span className="cb-occasion-text">Milestone birthdays</span></div>
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'\u{1F3C6}'}</span><span className="cb-occasion-text">Retirements</span></div>
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'\u{1F48D}'}</span><span className="cb-occasion-text">Anniversaries</span></div>
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'\u{1F490}'}</span><span className="cb-occasion-text">Mother's & Father's Day</span></div>
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'\u{1F54A}'}</span><span className="cb-occasion-text">Celebrate a life</span></div>
              <div className="cb-occasion"><span className="cb-occasion-emoji">{'✨'}</span><span className="cb-occasion-text">Just because</span></div>
            </div>
          </div>

          {/* Social proof — real Google reviews */}
          <Reviews title="Real families, real reviews" />

          {/* FAQ */}
          <div className="cb-faq">
            <div className="cb-section-head"><h2 className="cb-section-title">Common questions</h2></div>
            <details className="cb-faq-item">
              <summary className="cb-faq-q">Do the people contributing need an account?</summary>
              <div className="cb-faq-a">No. You share a link, and they add their story and photos right from it, no sign-up, no app to download. Only you, the organizer, need a free account.</div>
            </details>
            <details className="cb-faq-item">
              <summary className="cb-faq-q">How many people can contribute?</summary>
              <div className="cb-faq-a">As many as you like. Invite a few close family members or the whole extended family. You decide which stories make it into the final book.</div>
            </details>
            <details className="cb-faq-item">
              <summary className="cb-faq-q">What if someone isn't a writer?</summary>
              <div className="cb-faq-a">They just answer a simple question, by typing or talking out loud. Talebound shapes their words into a warm chapter and keeps their voice. No blank page, ever.</div>
            </details>
            <details className="cb-faq-item">
              <summary className="cb-faq-q">What does it cost?</summary>
              <div className="cb-faq-a">Starting and building the book is free. You only pay when you order the finished hardcover, printed in full color with free shipping in the USA.</div>
            </details>
            <details className="cb-faq-item">
              <summary className="cb-faq-q">How long does it take?</summary>
              <div className="cb-faq-a">It's up to you. Some families pull it together in a week, others gather stories over a month. There's no deadline, so give people time to write something meaningful.</div>
            </details>
          </div>

          {/* Final CTA */}
          <div className="cb-final">
            <h2 className="cb-final-title">Give them a book they'll talk about for years</h2>
            <p className="cb-final-sub">Start today, invite the family, and give them their whole story in one place.</p>
            <a href={START_URL} className="cb-cta" onClick={go('final')}>Start a Celebration Book</a>
          </div>
        </div>

        <div className="cb-footer">
          <div className="cb-footer-text">
            <a href="https://taleboundbook.com" className="cb-footer-link">Talebound</a>. Stories bound to last.
          </div>
          <div className="cb-footer-social">
            <a href="https://www.instagram.com/taleboundbook/" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://www.facebook.com/Taleboundbook" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Facebook">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

export default CelebrationPage;
